import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { CustomerSearchSelect } from '@/components/customers/CustomerSearchSelect.js';
import { SlideOver } from '@/components/SlideOver.js';
import { cloneTicket, type CloneTicketInput } from '@/lib/api/tickets.js';
import { createCustomer } from '@/lib/api/customers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { CustomerForm, type CustomerFormValues } from '@/pages/customers/CustomerForm.js';

type Props = {
  ticketId: string;
  ticketNumber: string;
  currentCustomerId?: string | null | undefined;
  open: boolean;
  onClose: () => void;
};

export function CloneTicketModal({
  ticketId,
  ticketNumber,
  currentCustomerId,
  open,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [includeCustomer, setIncludeCustomer] = useState(true);
  const [includeDevices, setIncludeDevices] = useState(true);
  const [includeRepairs, setIncludeRepairs] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [altCustomerId, setAltCustomerId] = useState<string | null>(null);
  const [altCustomerName, setAltCustomerName] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<{ id: string; ticketNumber: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CloneTicketInput) => cloneTicket(ticketId, body),
    onSuccess: (ticket) => {
      setCreated({ id: ticket.id, ticketNumber: ticket.ticketNumber });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => createCustomer(values),
    onSuccess: (row) => {
      const id = String(row.id);
      const first = typeof row.firstName === 'string' ? row.firstName : '';
      const last = typeof row.lastName === 'string' ? row.lastName : '';
      const name = [first, last].filter(Boolean).join(' ') || 'New customer';
      setAltCustomerId(id);
      setAltCustomerName(name);
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer-search-select'] });
    },
  });

  if (!open) return null;

  function submit() {
    const body: CloneTicketInput = {
      includeDevices,
      includeRepairs: includeDevices && includeRepairs,
      includeNotes,
      notes: notes.trim() || null,
    };
    if (!includeCustomer) {
      body.customerId = altCustomerId;
    } else if (currentCustomerId) {
      body.customerId = currentCustomerId;
    }
    mutation.mutate(body);
  }

  const errorMessage = mutation.isError
    ? (() => {
        const parsed = parseApiError(mutation.error);
        if (parsed.code === 'FORBIDDEN') {
          return 'Clone failed. Store manager role or higher is required.';
        }
        return parsed.message || 'Clone failed.';
      })()
    : null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
          {created ? (
            <>
              <h2 className="text-lg font-semibold text-slate-900">Ticket cloned</h2>
              <p className="mt-2 text-sm text-slate-600">
                Created {created.ticketNumber} from {ticketNumber}.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm"
                  onClick={() => {
                    setCreated(null);
                    onClose();
                  }}
                >
                  Stay
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => navigate(`/tickets/${created.id}`)}
                >
                  View New
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-slate-900">Clone {ticketNumber}</h2>
              <p className="mt-1 text-sm text-slate-500">
                Creates a new intake ticket linked to the original.
              </p>
              <div className="mt-4 space-y-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeCustomer}
                    onChange={(e) => {
                      setIncludeCustomer(e.target.checked);
                      if (e.target.checked) {
                        setAltCustomerId(null);
                        setAltCustomerName(null);
                      }
                    }}
                  />
                  Keep same customer
                </label>
                {!includeCustomer ? (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-medium text-slate-600">Assign a different customer</p>
                    <CustomerSearchSelect
                      selectedId={altCustomerId}
                      selectedName={altCustomerName}
                      {...(currentCustomerId ? { excludeCustomerId: currentCustomerId } : {})}
                      selectedSubtitle="Will be set on the cloned ticket"
                      placeholder="Search by name, phone, or email"
                      onSelect={(c) => {
                        setAltCustomerId(c.id);
                        setAltCustomerName(c.name);
                      }}
                      onClear={() => {
                        setAltCustomerId(null);
                        setAltCustomerName(null);
                      }}
                    />
                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => setCreateOpen(true)}
                    >
                      <Plus className="h-4 w-4" />
                      New customer
                    </button>
                    <p className="text-xs text-slate-500">
                      Leave empty to clone without a linked customer (walk-in / override only).
                    </p>
                  </div>
                ) : null}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeDevices}
                    onChange={(e) => setIncludeDevices(e.target.checked)}
                  />
                  Include devices
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeRepairs}
                    disabled={!includeDevices}
                    onChange={(e) => setIncludeRepairs(e.target.checked)}
                  />
                  Include repairs
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeNotes}
                    onChange={(e) => setIncludeNotes(e.target.checked)}
                  />
                  Include notes
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                  rows={2}
                  placeholder="Optional reason / notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
                {createMutation.isError ? (
                  <p className="text-sm text-red-600">{parseApiError(createMutation.error).message}</p>
                ) : null}
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={mutation.isPending}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  onClick={submit}
                >
                  {mutation.isPending ? 'Cloning…' : 'Clone ticket'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <SlideOver title="New Customer" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CustomerForm
          submitLabel="Create & select"
          loading={createMutation.isPending}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      </SlideOver>
    </>
  );
}
